import { Select } from 'antd';
import React from 'react';
import { FuelTypeEnum, getFuelTypeLabel } from '../../../../enums/FuelTypeEnum';
import { useTranslation } from 'react-i18next';
import InputFilterItem from '../../../delivery/components/InputFilterItem';
import { useFilterContainerClassName, useFilterInputContainerClassName } from '../../../../utils/languageUtils';
import { getSmartLabelClassName } from '../../../../utils/labelWidthUtils';

export interface FilterOptions {
  licensePlate: string;
  vehicleTypeId: number | null;
  carrierId: number | null;
  fuelType: number | null;
  status: number | null;
}

export const DEFAULT_FILTER_OPTIONS: FilterOptions = {
  licensePlate: '',
  vehicleTypeId: null,
  carrierId: null,
  fuelType: null,
  status: null,
};

interface FilterAreaProps {
  carrierOptions: { label: string; value: number; status?: number }[];
  modelOptions: { label: string; value: number; status?: number }[];
  statusOptions: { label: string; value: number }[];
  filters: FilterOptions;
  setFilters: (filters: FilterOptions) => void;
}

const FilterArea: React.FC<FilterAreaProps> = ({ carrierOptions, modelOptions, statusOptions, filters, setFilters }) => {
  const { t } = useTranslation();
  const containerClassName = useFilterContainerClassName();
  const inputContainerClassName = useFilterInputContainerClassName();
  
  // 定义列标签数组用于对齐计算
  // 第一列：车牌号、承运商；第二列：型号、状态；第三列：燃料类型
  const column1Labels = [t('tms.vehicle.filters.licensePlate'), t('tms.vehicle.filters.carrier')];
  const column2Labels = [t('tms.vehicle.filters.model'), t('tms.vehicle.filters.status')];
  const column3Labels = [t('tms.vehicle.filters.fuelType')];
  
  const activeCarrierOptions = carrierOptions.filter(c => c.status !== 0);
  
  const fuelTypeOptions = Object.values(FuelTypeEnum)
    .filter(v => typeof v === 'number')
    .map(v => ({ label: getFuelTypeLabel(v as FuelTypeEnum, t), value: v as number }));

  const handleFilterChange = (name: string, value: any) => {
    setFilters({ ...filters, [name]: value } as FilterOptions);
  };

  return (
    <div className="mb-2 pl-0 py-2">
      {/* 使用 Grid 布局确保列对齐 */}
      <div className="grid grid-cols-3 gap-x-10 gap-y-4 items-start">
        <div className="min-w-0">
          <InputFilterItem
            label={t('tms.vehicle.filters.licensePlate')}
            value={filters.licensePlate}
            onChange={e => handleFilterChange('licensePlate', e.target.value)}
            spanClassName={getSmartLabelClassName(column1Labels)}
          />
        </div>
        <div className="min-w-0">
          <div className={containerClassName}>
            <span className={getSmartLabelClassName(column2Labels)}>{t('tms.vehicle.filters.model')}</span>
            <div className={inputContainerClassName}>
              <Select
                placeholder={t('tms.vehicle.placeholders.all') as string}
                className="w-full"
                size="middle"
                options={modelOptions}
                value={filters.vehicleTypeId}
                onChange={value => handleFilterChange('vehicleTypeId', value)}
                allowClear
              />
            </div>
          </div>
        </div>
        <div className="min-w-0">
          <div className={containerClassName}>
            <span className={getSmartLabelClassName(column3Labels)}>{t('tms.vehicle.filters.fuelType')}</span>
            <div className={inputContainerClassName}>
              <Select
                placeholder={t('tms.vehicle.placeholders.all') as string}
                className="w-full"
                size="middle"
                options={fuelTypeOptions}
                value={filters.fuelType}
                onChange={value => handleFilterChange('fuelType', value)}
                allowClear
              />
            </div>
          </div>
        </div>
        {/* 第二行 */}
        <div className="min-w-0">
          <div className={containerClassName}>
            <span className={getSmartLabelClassName(column1Labels)}>{t('tms.vehicle.filters.carrier')}</span>
            <div className={inputContainerClassName}>
              <Select
                placeholder={t('tms.vehicle.placeholders.all') as string}
                className="w-full"
                size="middle"
                options={activeCarrierOptions}
                value={filters.carrierId}
                onChange={value => handleFilterChange('carrierId', value)}
                allowClear
              />
            </div>
          </div>
        </div>
        <div className="min-w-0">
          <div className={containerClassName}>
            <span className={getSmartLabelClassName(column2Labels)}>{t('tms.vehicle.filters.status')}</span>
            <div className={inputContainerClassName}>
              <Select
                placeholder={t('tms.vehicle.placeholders.all') as string}
                className="w-full"
                size="middle"
                options={statusOptions}
                value={filters.status}
                onChange={value => handleFilterChange('status', value)}
                allowClear
              />
            </div>
          </div>
        </div>
        <div className="min-w-0"></div>
      </div>
    </div>
  );
};

export default FilterArea;
